import { CalendarConnectionAuthorizeResponseSchema, CalendarConnectionDetailSchema, CalendarConnectionListResponseSchema, DisconnectCalendarConnectionResponseSchema, StoredCalendarEventSchema, StoredCalendarEventsResponseSchema, ManualCalendarMatchResponseSchema, DeleteManualCalendarMatchResponseSchema, PromoteCalendarMatchResponseSchema, } from '@packages/types';
import { api, apiRaw } from '../client.js';
export function listCalendarConnections(accountId, params) {
    const query = new URLSearchParams();
    if (params?.cursor)
        query.append('cursor', params.cursor);
    if (params?.limit !== undefined)
        query.append('limit', params.limit.toString());
    const qs = query.toString();
    return api(qs
        ? `/accounts/${accountId}/calendar-connections?${qs}`
        : `/accounts/${accountId}/calendar-connections`, CalendarConnectionListResponseSchema);
}
export function getCalendarConnectionAuthUrl(accountId, provider, frontendUrl) {
    return api(`/accounts/${accountId}/calendar-connections/connect/${provider}/authorize?frontendUrl=${encodeURIComponent(frontendUrl)}`, CalendarConnectionAuthorizeResponseSchema);
}
export function handleCalendarConnectionCallback(accountId, provider, body) {
    return api(`/accounts/${accountId}/calendar-connections/connect/${provider}/callback`, CalendarConnectionDetailSchema, {
        method: 'POST',
        body: {
            code: body.code,
            state: body.state,
            ...(body.lookbackDays !== undefined && { lookbackDays: body.lookbackDays }),
        },
    });
}
export function disconnectCalendarConnection(accountId, calendarConnectionId) {
    return api(`/accounts/${accountId}/calendar-connections/${calendarConnectionId}`, DisconnectCalendarConnectionResponseSchema, { method: 'DELETE' });
}
export function createManualCalendarMatch(accountId, storedCalendarEventId, body) {
    return api(`/accounts/${accountId}/stored-calendar-events/${storedCalendarEventId}/manual-matches`, ManualCalendarMatchResponseSchema, { method: 'POST', body });
}
export function deleteManualCalendarMatch(accountId, storedCalendarEventId, matchId) {
    return api(`/accounts/${accountId}/stored-calendar-events/${storedCalendarEventId}/manual-matches/${matchId}`, DeleteManualCalendarMatchResponseSchema, { method: 'DELETE' });
}
export function promoteCalendarMatch(accountId, storedCalendarEventId, matchId) {
    return api(`/accounts/${accountId}/stored-calendar-events/${storedCalendarEventId}/manual-matches/${matchId}/promote`, PromoteCalendarMatchResponseSchema, { method: 'POST' });
}
/**
 * Confirm a live proposal, promoting it to the event's chosen allocation. The
 * `matchId` names the exact proposal the user saw - see
 * `confirmEmailProposal` for why "confirm whatever is live" is unsafe.
 *
 * The route returns 409 `PROPOSAL_SUPERSEDED` when a different proposal is
 * now live and 409 `ALREADY_ALLOCATED` when the event was allocated (to any
 * match) since the proposal was read; both are `ApiError`, not a thrown plain
 * `Error`, so callers branch on `err.code`. Calendar has no second question
 * (no billable dimension), so this takes only the
 * `matchId` that email's `ConfirmProposalInputSchema` extends.
 */
export async function confirmCalendarProposal(accountId, storedCalendarEventId, matchId) {
    await apiRaw(`/accounts/${accountId}/stored-calendar-events/${storedCalendarEventId}/proposal/confirm`, { method: 'POST', body: { matchId } });
    return { success: true };
}
/**
 * Dismiss a live proposal. Soft-hides it (the row's `dismissedAt` never
 * clears); the event stays unallocated and offerable for a different match.
 * Same `matchId` contract as {@link confirmCalendarProposal}.
 */
export async function dismissCalendarProposal(accountId, storedCalendarEventId, matchId) {
    await apiRaw(`/accounts/${accountId}/stored-calendar-events/${storedCalendarEventId}/proposal/dismiss`, { method: 'POST', body: { matchId } });
    return { success: true };
}
export function listStoredCalendarEvents(accountId, calendarConnectionId, params) {
    const query = new URLSearchParams();
    if (params?.cursor)
        query.append('cursor', params.cursor);
    if (params?.limit !== undefined)
        query.append('limit', params.limit.toString());
    if (params?.startFrom)
        query.append('startFrom', params.startFrom);
    if (params?.startTo)
        query.append('startTo', params.startTo);
    const qs = query.toString();
    return api(qs
        ? `/accounts/${accountId}/calendar-connections/${calendarConnectionId}/events?${qs}`
        : `/accounts/${accountId}/calendar-connections/${calendarConnectionId}/events`, StoredCalendarEventsResponseSchema);
}
/**
 * List stored calendar events across every connection in an account, with one
 * globally-sorted cursor and the same filter set as `getAllStoredEmails`.
 *
 * Server-side enforces the per-member visibility rule: regular members see
 * only events from connections they personally connected; assistants and
 * owners see every connection in the account.
 */
export function getAllStoredCalendarEvents(accountId, params) {
    const query = new URLSearchParams();
    if (params?.cursor)
        query.append('cursor', params.cursor);
    if (params?.limit !== undefined)
        query.append('limit', params.limit.toString());
    if (params?.ids !== undefined) {
        for (const id of params.ids)
            query.append('ids', id);
    }
    if (params?.startFrom)
        query.append('startFrom', params.startFrom);
    if (params?.startTo)
        query.append('startTo', params.startTo);
    if (params?.clientId)
        query.append('clientId', params.clientId);
    if (params?.clientProjectId)
        query.append('clientProjectId', params.clientProjectId);
    if (params?.matchState)
        query.append('matchState', params.matchState);
    const qs = query.toString();
    return api(qs
        ? `/accounts/${accountId}/stored-calendar-events?${qs}`
        : `/accounts/${accountId}/stored-calendar-events`, StoredCalendarEventsResponseSchema);
}
/**
 * Get a single stored calendar event by id - including chosenMatch + activity
 * state. Used by the activity diary to render an inline preview of the
 * calendar event a CALENDAR-sourced activity was logged from.
 */
export function getStoredCalendarEvent(accountId, storedCalendarEventId) {
    return api(`/accounts/${accountId}/stored-calendar-events/${storedCalendarEventId}`, StoredCalendarEventSchema);
}
